import { useCallback, useEffect, useState } from 'react';

import { Avatar, Button, Card, Col, Row, Select, Switch, Tooltip, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import CustomMaskedInput from 'components/MaskedInput/index';
import type ICoProducer from 'interfaces/ICoProducer';
import Truncate from 'v2/components/Shared/TruncateText';
import { run } from 'validations/ruleRunner';

import Validations from '../CoProducerForm/validations';
import { ApprobationStatus, PartnerStatus } from '../Status';

interface IProps {
  coproducer: ICoProducer;
  onChangeRequiredFields: (...args: any[]) => any;
  onSearchSuccess: (...args: any[]) => any;
  index: number;
  disabled?: boolean;
  isProducer: boolean;
  onChangeField: (...args: any[]) => any;
  onRemove: (...args: any[]) => any;
  pendingStatus?: string;
  coproducers: Array<ICoProducer>;
}

const PROJECT_ELEVAR_ID = 75879698;

function PartnerCardPreview(props: IProps) {
  const { coproducer, index, disabled, isProducer, onChangeField, onRemove, onChangeRequiredFields } = props;

  const [validationErrors, setValidationErrors] = useState<any>({});
  const [permissions, setPermissions] = useState(false);

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isValid = useCallback((field: any) => !!validationErrors[field], [validationErrors]);

  useEffect(() => {
    const newValidationErrors = run(coproducer, Validations());
    setValidationErrors(newValidationErrors);
  }, [coproducer]);

  const runValidation = useCallback(() => {
    const newValidationErrors = run(coproducer, Validations());
    setValidationErrors(newValidationErrors);

    if (onChangeRequiredFields) {
      onChangeRequiredFields(
        'coProducerRequiredFields',
        newValidationErrors ? Object.keys(newValidationErrors).length : 0
      );
    }
  }, [coproducer, onChangeRequiredFields]);

  const onTooglePermissions = useCallback(() => {
    setPermissions(prev => !prev);
  }, []);

  const showMoreElevar = useCallback(() => {
    const ref = window.open('https://www.projetoelevar.org.br/#sobre-o-projeto', '_blank');
    ref.opener = null;
  }, []);

  const onChange = useCallback(
    (field: any, fieldIndex: any, value: any) => {
      if (onChangeField) {
        onChangeField(field, fieldIndex, value);
        runValidation();
      }
    },
    [onChangeField, runValidation]
  );

  const commissionType = coproducer.comission_type || 'P';
  const permissionsIcon = permissions ? <Icon.ChevronUp  /> : <Icon.ChevronDown  />;
  const permissionsText = permissions ? 'Esconder' : 'Mostrar';
  const isActive = coproducer.active || coproducer.new;
  const isAffiliatesSelectedAndNFNotSelected = coproducer.only_over_affiliates && !coproducer.fiscal_document;
  const isNFSelectedAndAffiliatesNotSelected = !coproducer.only_over_affiliates && coproducer.fiscal_document;

  const handleRemoveClick = () => {
    if (!isProducer) {
      onRemove(index);
    }
  };

  return (
    <Card size='small' variant='borderless' className='bg-(--uizz-antd-color-fill-alter)'>
      <div className='flex items-center gap-3'>
        <Avatar
          className={isActive ? 'bg-(--uizz-antd-color-success)' : 'bg-(--uizz-antd-color-warning)'}
          src={
            coproducer.customer_id === PROJECT_ELEVAR_ID
              ? 'https://cdn.eduzzcdn.com/myeduzz/upload/b0/99/b0995f1f0c7e4ad490e6f85f5be5f6c9'
              : undefined
          }
          icon={<Icon.Users  />}
        />
        <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <div className='font-medium'>
            <Truncate final='...' count={50}>
              {coproducer.business_name}
            </Truncate>
          </div>
          <div className='text-content-subtitle text-base'>
            <Truncate final='...' count={50}>
              {coproducer.email}
            </Truncate>
          </div>
        </div>
        <div>
          {!coproducer.pending && !coproducer.is_producer && (
            <Tooltip title='Remover parceiro' placement='top'>
              <Button type='text' danger icon={<Icon.Trash2  />} onClick={handleRemoveClick} />
            </Tooltip>
          )}
          {coproducer.is_producer && (
            <Tooltip title='Produtor' placement='top'>
              <Button type='text' disabled icon={<Typography.Text className='text-base'><Icon.Activity /></Typography.Text>} />
            </Tooltip>
          )}
        </div>
      </div>

      <div className='mt-3 pb-4'>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Typography.Text className='mb-1 block text-base'>Tipo de comissão</Typography.Text>
            <Select
              className='w-full'
              size='small'
              disabled={disabled || commissionType === 'P'}
              value={commissionType || 'P'}
              onChange={value => onChange('comission_type', index, value)}
              options={[
                { value: 'P', label: 'Porcentagem' },
                ...(commissionType === 'V' ? [{ value: 'V', label: 'Valor', disabled: true }] : [])
              ]}
            />
          </Col>
          <Col xs={24} md={12}>
            {commissionType === 'P' && (
              <CustomMaskedInput
                disabled={disabled}
                noStrictMaxPercent={!disabled}
                mask='percent'
                label='Valor'
                helperText={isValid('comission_value') ? errorFor('comission_value') : null}
                error={isValid('comission_value')}
                startAdornment='%'
                fullWidth={true}
                disableUnderline={true}
                onChange={(value: any) => onChange('comission_value', index, value)}
                value={String(coproducer.comission_value)}
                InputLabelProps={{
                  shrink: true
                }}
              />
            )}

            {commissionType === 'V' && (
              <CustomMaskedInput
                disabled={disabled}
                mask='money'
                label='Valor'
                helperText={isValid('comission_value') ? errorFor('comission_value') : null}
                error={isValid('comission_value')}
                startAdornment='R$'
                disableUnderline={true}
                fullWidth={true}
                onChange={(value: any) => onChange('comission_value', index, value)}
                value={String(coproducer.comission_value)}
                InputLabelProps={{
                  shrink: true
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div className='flex items-center gap-2 justify-between'>
        <div className='flex items-center gap-2'>
          <PartnerStatus cop={coproducer} />
          {coproducer.pending && <ApprobationStatus cop={coproducer} />}
        </div>

        {!coproducer.pending && !coproducer.is_producer && (
          <>
            {coproducer.customer_id === PROJECT_ELEVAR_ID ? (
              <Button onClick={showMoreElevar}>Saiba mais</Button>
            ) : (
              <Button onClick={onTooglePermissions}>
                {`${permissionsText} permissões`} {permissionsIcon}
              </Button>
            )}
          </>
        )}

      </div>

      {permissions && (
        <div className='px-4 pb-4'>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div className='flex items-center gap-2'>
                <Switch
                  disabled={disabled || isProducer || isNFSelectedAndAffiliatesNotSelected}
                  checked={coproducer.only_over_affiliates}
                  onChange={checked => onChangeField('only_over_affiliates', index, checked)}
                />
                <Typography.Text className='text-base'>Afiliados</Typography.Text>
              </div>
              <Typography.Paragraph className='text-content-subtitle mt-1 text-base'>
                O coprodutor será comissionado somente sobre vendas realizadas através de afiliados.{' '}
                <a
                  href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807551969-Como-adicionar-um-Co-Produtor-%C3%93rbita-'
                  target='blank'
                >
                  Saiba mais
                </a>
                {isNFSelectedAndAffiliatesNotSelected && (
                  <Typography.Paragraph className='text-base text-(--uizz-antd-color-error)'>
                    Não é possível ter &quot;Afiliados&quot; e &quot;Emitir NF&quot; marcados ao mesmo tempo.
                  </Typography.Paragraph>
                )}
              </Typography.Paragraph>
            </Col>
            <Col span={24}>
              <div className='flex items-center gap-2'>
                <Switch
                  disabled={disabled || isProducer}
                  checked={coproducer.affiliate_manager}
                  onChange={checked => onChangeField('affiliate_manager', index, checked)}
                />
                <Typography.Text className='text-base'>Gerir afiliados</Typography.Text>
              </div>
              <Typography.Paragraph className='text-content-subtitle mt-1 text-base'>
                O coprodutor consegue: aprovar/ reprovar afiliações, ver relatório de afiliados e conversar com
                afiliados de todas as variações.
              </Typography.Paragraph>
            </Col>

            <Col span={24}>
              <div className='flex items-center gap-2'>
                <Switch
                  disabled={disabled || isProducer || isAffiliatesSelectedAndNFNotSelected}
                  checked={coproducer.fiscal_document}
                  onChange={checked => onChangeField('fiscal_document', index, checked)}
                />
                <Typography.Text className='text-base'>Emitir NF</Typography.Text>
              </div>
              <Typography.Paragraph className='text-content-subtitle mt-1 text-base'>
                O coprodutor também será responsável por emitir Nota Fiscal aos clientes.{' '}
                <a
                  href='https://ajuda.eduzz.com/hc/pt-br/articles/4402651955739-Como-Funciona-a-Central-de-Documentos-Fiscais-%C3%93rbita-'
                  target='blank'
                >
                  Saiba Mais
                </a>
                {isAffiliatesSelectedAndNFNotSelected && (
                  <Typography.Paragraph className='text-base text-(--uizz-antd-color-error)'>
                    Não é possível ter &quot;Afiliados&quot; e &quot;Emitir NF&quot; marcados ao mesmo tempo.
                  </Typography.Paragraph>
                )}
              </Typography.Paragraph>
            </Col>

            <Col span={24}>
              <div className='flex items-center gap-2'>
                <Switch
                  disabled={disabled || isProducer}
                  checked={coproducer.permission_refund}
                  onChange={checked => onChangeField('permission_refund', index, checked)}
                />
                <Typography.Text className='text-base'>Reembolsar</Typography.Text>
              </div>
              <Typography.Paragraph className='text-content-subtitle mt-1 text-base'>
                Quando habilitado, permite o coprodutor reembolsar faturas.
              </Typography.Paragraph>
            </Col>
            <Col span={24}>
              <div className='flex items-center gap-2'>
                <Switch
                  disabled={disabled || isProducer}
                  checked={coproducer.visibility_customer_email}
                  onChange={checked => onChangeField('visibility_customer_email', index, checked)}
                />
                <Typography.Text className='text-base'>Ver cliente</Typography.Text>
              </div>
              <Typography.Paragraph className='text-content-subtitle mt-1 text-base'>
                O coprodutor poderá visualizar os dados dos clientes nos relatórios.
              </Typography.Paragraph>
            </Col>
          </Row>
        </div>
      )}
    </Card>
  );
}

export default PartnerCardPreview;
